import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import buddhistEra from 'dayjs/plugin/buddhistEra';
import 'dayjs/locale/th';
import type { ISalesSummaryCreatedAt } from '../compiler/salesTimestamp.interface';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(buddhistEra);

export const APP_TIMEZONE = 'Asia/Bangkok';
const TIMEZONE_SUFFIX_PATTERN = /([zZ]|[+\-]\d{2}:?\d{2})$/;

// For instant endpoints that use UTC when an offset is omitted. The sales-summary
// legacy formatter has a different contract; use its dedicated helper below.
export const toBangkokDayjs = (value?: string | number | Date | Dayjs | null): Dayjs | null => {
  if (value === undefined || value === null || value === '') return null;

  if (dayjs.isDayjs(value)) return value.isValid() ? value.tz(APP_TIMEZONE) : null;

  if (typeof value === 'number' || value instanceof Date) {
    const parsedNumber = dayjs(value);
    return parsedNumber.isValid() ? parsedNumber.tz(APP_TIMEZONE) : null;
  }

  const rawValue = String(value).trim();
  if (!rawValue) return null;

  const parsed = TIMEZONE_SUFFIX_PATTERN.test(rawValue) ? dayjs(rawValue) : dayjs.utc(rawValue);
  return parsed.isValid() ? parsed.tz(APP_TIMEZONE) : null;
};

export const formatBangkokDateTime = (
  value?: string | number | Date | Dayjs | null,
  format = 'DD/MM/BBBB HH:mm น.',
): string => toBangkokDayjs(value)?.locale('th').format(format) || '-';

// Compatibility for older sales-summary responses without provenance metadata.
// New responses resolve the mixed database timestamp contracts in SQL; the
// server-formatted legacy string cannot establish the original instant.
export const toSalesSummaryCreatedAt = (
  header?: ISalesSummaryCreatedAt | null,
): Dayjs | null => {
  if (header?.createdAtBasis === 'unverified' || header?.createdAtBasis === 'date_only') return null;
  const iso = header?.createdAtIso?.trim();
  if (iso && TIMEZONE_SUFFIX_PATTERN.test(iso)) {
    const instant = toBangkokDayjs(iso);
    if (instant) return instant;
  }
  // A new response explicitly reports whether the timestamp can be resolved.
  // Never revive its legacy value after the API has withheld an instant.
  if (header?.createdAtBasis != null) return null;
  const legacy = header?.createdAt?.trim();
  if (!legacy) return null;
  if (TIMEZONE_SUFFIX_PATTERN.test(legacy)) return toBangkokDayjs(legacy);
  if (!/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(legacy)) return null;
  const wallClock = dayjs.utc(legacy);
  if (!wallClock.isValid() || wallClock.format('YYYY-MM-DD HH:mm:ss') !== legacy.slice(0, 19).replace('T', ' ')) return null;
  return dayjs.tz(legacy, APP_TIMEZONE);
};

export const formatSalesSummaryCreatedAt = (
  header?: ISalesSummaryCreatedAt | null,
  format = 'DD/MM/BBBB HH:mm น.',
): string => {
  const instant = toSalesSummaryCreatedAt(header);
  if (instant) return instant.locale('th').format(format);
  if (header?.createdAtBasis == null) return '-';
  const tokens = format.replace(/\[[^\]]*\]/g, '');
  if (!/[DMYB]/.test(tokens)) return '-';
  if (/[HhmsSaA]/.test(tokens)) {
    const date = formatBusinessDate(header.createdAtDate);
    return date === '-' ? '-' : `${date} (ไม่ระบุเวลา)`;
  }
  return formatBusinessDate(header.createdAtDate, format);
};

/** Detail/CMS/history legacy fields contain instants, unlike old summary strings. */
export const formatOrderCreatedAt = (
  order?: ISalesSummaryCreatedAt | null,
  format = 'DD/MM/BBBB HH:mm น.',
): string => {
  if (order?.createdAtBasis != null) return formatSalesSummaryCreatedAt(order, format);
  return formatBangkokDateTime(order?.createdAtIso || order?.createdAt || order?.created_at, format);
};

// Human-readable defaults use Buddhist years; explicit machine masks remain ISO/Gregorian.
// Calendar fields carry a business date, even when older endpoints append a
// midnight timestamp. Preserve the date component instead of applying an offset.
export const formatBusinessDate = (
  value?: string | null,
  format = 'DD/MM/BBBB',
): string => {
  if (!value) return '-';
  const datePart = value.trim().match(/^(\d{4}-\d{2}-\d{2})(?:$|T|\s)/)?.[1];
  if (!datePart) return '-';
  const parsed = dayjs.utc(datePart);
  if (!parsed.isValid() || parsed.format('YYYY-MM-DD') !== datePart) return '-';
  return parsed.locale('th').format(format);
};

/** Calendar labels from pickers/month selectors never shift the selected day.
 * Strings preserve their calendar component, including legacy midnight suffixes.
 * Only the rendered year changes; the underlying date remains Gregorian.
 */
export const formatThaiCalendarDate = (
  value?: string | number | Date | Dayjs | null,
  format = 'DD/MM/BBBB',
): string => {
  if (value == null || value === '') return '-';
  if (typeof value === 'string') {
    const raw = value.trim();
    return formatBusinessDate(/^\d{4}-\d{2}$/.test(raw) ? `${raw}-01` : raw, format);
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.locale('th').format(format) : '-';
};
